import { useState, useRef, useEffect } from "react";
import * as React from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Upload, Camera } from "lucide-react";
import { toast } from "sonner";

const ProfileImageUpload = ({ currentImage, userName, onImageUpdate }) => {
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [imgSrc, setImgSrc] = useState("");
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [isMounted, setIsMounted] = useState(true);

  // Cleanup on unmount
  React.useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImgSrc(reader.result?.toString() || "");
        setShowCropDialog(true);
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 90,
        },
        1,
        width,
        height
      ),
      width,
      height
    );
    setCrop(crop);
  };

  const getCroppedImg = async () => {
    if (!completedCrop || !imgRef.current) {
      return null;
    }

    const image = imgRef.current;
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    // Set canvas size to desired output (200x200)
    canvas.width = 200;
    canvas.height = 200;
    
    const ctx = canvas.getContext("2d");
    
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      200,
      200
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            toast.error("Resim işlenemedi");
            return;
          }
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => {
            resolve(reader.result);
          };
        },
        "image/jpeg",
        0.8
      );
    });
  };

  const handleUpload = async () => {
    try {
      setUploading(true);
      const croppedImage = await getCroppedImg();
      
      if (!croppedImage) {
        if (isMounted) {
          toast.error("Resim kırpılamadı");
          setUploading(false);
        }
        return;
      }

      await onImageUpdate(croppedImage);
      
      if (isMounted) {
        setShowCropDialog(false);
        setImgSrc("");
        toast.success("Profil resmi güncellendi");
      }
    } catch (error) {
      console.error("Upload error:", error);
      if (isMounted) {
        toast.error("Resim yüklenemedi");
      }
    } finally {
      if (isMounted) {
        setUploading(false);
      }
    }
  };

  return (
    <>
      <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
        <Avatar className="h-32 w-32 border-4 border-white shadow-lg">
          <AvatarImage src={currentImage} />
          <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-green-600 text-white text-4xl font-bold">
            {userName?.charAt(0)}
          </AvatarFallback>
        </Avatar>
        
        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Camera className="h-8 w-8 text-white" />
        </div>
        
        <div className="absolute bottom-0 right-0 bg-emerald-600 rounded-full p-2 border-2 border-white shadow-lg">
          <Upload className="h-4 w-4 text-white" />
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={onSelectFile}
        className="hidden"
      />

      <Dialog open={showCropDialog} onOpenChange={setShowCropDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Profil Resmini Düzenle</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {imgSrc && (
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
                circularCrop
              >
                <img
                  ref={imgRef}
                  alt="Crop"
                  src={imgSrc}
                  onLoad={onImageLoad}
                  className="max-h-96"
                />
              </ReactCrop>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCropDialog(false);
                setImgSrc("");
              }}
              disabled={uploading}
            >
              İptal
            </Button>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? "Yükleniyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProfileImageUpload;
